import { Sparkles } from 'lucide-react';
import { useI18n } from '@/i18n/I18nProvider';
import { Button, Dialog, DialogContent, Skeleton } from '@/shared/ui';
import type { ChangelogEntryDto } from '@nexops/shared';
import { ChangelogView } from './ChangelogView';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: ChangelogEntryDto[] | undefined;
  loading?: boolean;
  /** Tytuł: „Co nowego” albo „Zaktualizowano do vX”. */
  title?: string;
}

/** Okno „Co nowego” — wpisy dziennika zmian; z przyciskiem „Rozumiem”. */
export function ChangelogDialog({ open, onOpenChange, entries, loading, title }: Props) {
  const { t } = useI18n();
  const heading = title ?? t('appu.changelog.title');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={heading}
        description={t('appu.changelog.description')}
        closeLabel={t('common.close')}
        hideHeader
        className="max-w-xl"
        footer={
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            {t('appu.changelog.ok')}
          </Button>
        }
      >
        <div aria-hidden className="-mx-5 -mt-4 mb-4 flex items-center gap-3 border-b border-border px-5 py-4 pr-12">
          <span className="brand-gradient flex size-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm">
            <Sparkles className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[16px] font-extrabold leading-tight">{heading}</p>
            <p className="mt-0.5 text-[13px] text-muted-foreground">{t('appu.changelog.description')}</p>
          </div>
        </div>
        <div className="relative -mx-5">
          <div className="max-h-[56vh] overflow-y-auto px-5 pb-6">
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
              </div>
            ) : (
              <ChangelogView entries={entries ?? []} />
            )}
          </div>
          <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-popover to-transparent" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
